import { I18nProvider, ThemeProvider, Toast } from "@norican/react";
import type { ReactNode } from "react";

import { useFileDropGuard } from "@/hooks";
import { useLanguage } from "@/i18n";

import { SiteBackground } from "./site-background";
import { Toaster } from "./toaster";

export interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Every context the app renders inside, in the order they must nest.
 *
 * `ThemeProvider` establishes the `.norican-root` scope that portaled surfaces
 * (menus, dialogs, toasts) attach to, so it has to be outermost. `I18nProvider`
 * feeds the active locale to every react-aria control — date pickers, calendars
 * and number fields read their segment order, first day of week and grouping
 * from it, which is why it takes the full BCP-47 locale (`en-GB`, not `en`)
 * rather than the i18next language key.
 *
 * The locale comes from app state, so this must sit *inside* whatever renders
 * it and *outside* the router. `Toast.Provider` wraps the router so a mutation
 * on any page can raise a toast that survives navigation.
 */
export function AppProviders({ children }: AppProvidersProps) {
  const { locale } = useLanguage();

  /* Window-level, and mounted for as long as the app is. A file dropped a few
     pixels outside an uploader would otherwise make the browser navigate to it
     and unload the SPA, taking the half-written ticket with it. Zones that do
     accept the drop mark the event handled, so this never intercepts a real
     one. */
  useFileDropGuard();

  return (
    <ThemeProvider theme="light" density="comfortable">
      <SiteBackground />
      <I18nProvider locale={locale}>
        <Toast.Provider>
          {children}
          <Toaster />
        </Toast.Provider>
      </I18nProvider>
    </ThemeProvider>
  );
}
